import React from "react";
import { loadSoccerAdminResearch } from "./api/soccerAdminResearch";

const STATUS_TONES = {
  "CUSTOMER READY": ["#DCFCE7", "#166534"],
  "RESEARCH ONLY": ["#DBEAFE", "#1D4ED8"],
  WITHHELD: ["#FEF3C7", "#92400E"],
  UNSUPPORTED: ["#FEE2E2", "#991B1B"],
  "MISSING HISTORY": ["#F3E8FF", "#6B21A8"],
  "PASS / NO PLAY": ["#E2E8F0", "#334155"],
};

const display = (value) => value === null || value === undefined || value === "" ? "—" : String(value);

export default function SoccerAdminResearchPanel({ active, isAdmin, identity }) {
  const [filters, setFilters] = React.useState({ competition: "", game_id: "", status: "", withholding_reason: "", prop_type: "", player: "", page: 1, page_size: 50 });
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!active || !isAdmin) {
      setPayload(null);
      setError("");
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    loadSoccerAdminResearch(filters, { identity })
      .then((next) => { if (!cancelled) setPayload(next); })
      .catch((nextError) => {
        if (!cancelled) {
          setPayload(null);
          setError(nextError?.status === 403 ? "Administrator access is required." : "Soccer admin research is unavailable.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, isAdmin, identity, JSON.stringify(filters)]);

  if (!active || !isAdmin) return null;
  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value, page: key === "page" ? value : 1 }));
  const rows = payload?.rows || [];
  const totalPages = payload ? Math.max(1, Math.ceil(payload.total_rows / payload.page_size)) : 1;

  return (
    <section data-testid="soccer-admin-research" style={{ margin: "18px 0", padding: 16, border: "2px solid #7C3AED", borderRadius: 16, background: "var(--card-bg, #fff)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0 }}>Soccer Admin Research</h2>
          <div style={{ color: "#64748B", fontSize: 12 }}>Private research contract · {payload?.schema_version || "loading"}</div>
        </div>
        {payload && <strong>{payload.total_rows.toLocaleString()} matching rows</strong>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginTop: 12 }}>
        <select aria-label="Research competition" value={filters.competition} onChange={(event) => setFilter("competition", event.target.value)}>
          <option value="">All competitions</option>
          {['Premier League','La Liga','Serie A','Ligue 1'].map((value) => <option key={value}>{value}</option>)}
        </select>
        <select aria-label="Research status" value={filters.status} onChange={(event) => setFilter("status", event.target.value)}>
          <option value="">All statuses</option>
          {Object.keys(STATUS_TONES).map((value) => <option key={value}>{value}</option>)}
        </select>
        <input aria-label="Research game ID" placeholder="Game ID" value={filters.game_id} onChange={(event) => setFilter("game_id", event.target.value)} />
        <input aria-label="Research prop type" placeholder="Prop type" value={filters.prop_type} onChange={(event) => setFilter("prop_type", event.target.value)} />
        <input aria-label="Research withholding reason" placeholder="Withholding reason" value={filters.withholding_reason} onChange={(event) => setFilter("withholding_reason", event.target.value)} />
        <input aria-label="Research player" placeholder="Player search" value={filters.player} onChange={(event) => setFilter("player", event.target.value)} />
      </div>
      {loading && <p>Loading private Soccer research…</p>}
      {error && <div role="alert" style={{ marginTop: 12, padding: 12, background: "#FEF2F2", color: "#991B1B", borderRadius: 10 }}>{error}</div>}
      {!loading && !error && payload && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 10, marginTop: 14 }}>
            {rows.map((row) => {
              const status = row["Readiness Status"] || "WITHHELD";
              const [background, color] = STATUS_TONES[status] || STATUS_TONES.WITHHELD;
              const actionable = status === "CUSTOMER READY";
              return (
                <article key={row["Projection ID"]} data-testid="soccer-admin-research-row" style={{ border: "1px solid #CBD5E1", borderRadius: 12, padding: 12 }}>
                  <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 999, background, color, fontSize: 11, fontWeight: 900 }}>{status}</span>
                  <h3 style={{ margin: "8px 0 2px" }}>{display(row.Player)}</h3>
                  <div>{display(row.Team)} vs {display(row.Opponent)} · {display(row.Competition)}</div>
                  <div style={{ marginTop: 6 }}><strong>{display(row["Prop Type"])}</strong> · Line {display(row["Prop Value"])}</div>
                  <div>Recommendation: {actionable ? display(row.Recommendation) : "—"}</div>
                  <div>Confidence: {actionable ? display(row["Guru Confidence"]) : "—"}</div>
                  <div>Benchmark: {display(row.Benchmark)}</div>
                  <div>Edge: {actionable ? display(row["Guru Edge Score"]) : "—"}</div>
                  <div style={{ marginTop: 6, fontSize: 12, overflowWrap: "anywhere" }}>Reasons: {(row["Withholding Reasons"] || []).join(" · ") || "None"}</div>
                  <div style={{ marginTop: 5, fontSize: 11, color: "#64748B" }}>Projection {row["Projection ID"]} · Game {display(row["Game ID"])}</div>
                </article>
              );
            })}
          </div>
          {!rows.length && <p>No research rows match these filters.</p>}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 14 }}>
            <button type="button" disabled={filters.page <= 1} onClick={() => setFilter("page", filters.page - 1)}>Previous</button>
            <span>Page {filters.page} of {totalPages}</span>
            <button type="button" disabled={filters.page >= totalPages} onClick={() => setFilter("page", filters.page + 1)}>Next</button>
          </div>
        </>
      )}
    </section>
  );
}
